import React from 'react'
import dayjs from 'dayjs'
import { Button, Flex, FloatButton, Form, Row, Col, Modal, Typography, message, Tooltip, Spin } from 'antd'
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { Plus, Save, RotateCcw } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'

import { CardState } from '../../types/card'
import { CarouselContent } from '../../../types/carousel'
import { MediaData } from '../../../types/media-data'

import { useCardContext, createInitialCards, INITIAL_REQUIRED_CARDS } from '../context/card'

import { useFetchCarouselData } from '../hooks/carousel/use-fetch-carousel'
import { useSaveCarouselContent } from '../hooks/carousel/use-save-carousel'
import { useExternalMediaMetadata } from '../hooks/media/use-external-media'
import { useUploadMedia } from '../hooks/carousel/use-upload-media'

import { HelpModal } from '../components/content/help-modal'
import { SortableCard } from '../components/content/sortable-card'
import { SecondaryButton } from '../components/ui/secondary-button'
import { StyledDatePicker } from '../components/ui/datepicker'

import formatDate from '../utils/date-formatter'
import sanitizeUrl from '../utils/url-sanitizer'
import { getPosterState } from '../utils/poster-state'
import {
  detectPlatformFromUrl,
  getPosterCdnUrl,
  removeOptimizedPath,
  extractUrlsNeedingMetadata,
  isExternalPlatformUrl
} from '../utils/media-utils'

const today = dayjs()

export const ContentPage: React.FC<{ widgetId: string }> = ({ widgetId }) => {
  const [form] = Form.useForm()
  const date = Form.useWatch('date', form)

  const { state, dispatch } = useCardContext()
  const { initialCards, cards, isFormModified, pendingPosters } = state

  const { fetchCarouselData, loading } = useFetchCarouselData({ widgetId })
  const { saveCarouselContent } = useSaveCarouselContent({ widgetId })
  const { fetchExternalMetadata } = useExternalMediaMetadata()
  const { uploadPoster } = useUploadMedia()

  const [isModalVisible, setIsModalVisible] = React.useState(false)
  const [isHelpModalOpen, setHelpModalOpen] = React.useState<boolean>(false)
  const [isFormCompleted, setIsFormCompleted] = React.useState(false)

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const isDateInPast = React.useMemo(() => {
    if (!date) return false
    const selectedDate = dayjs(date).format('YYYY-MM-DD')
    const today = dayjs().format('YYYY-MM-DD')
    return selectedDate < today
  }, [date])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    if (isDateInPast) return
    dispatch({ type: 'DRAG_END', payload: event })
  }

  const handleAddCard = () => {
    dispatch({ type: 'ADD_CARD' })
  }

  const handleDeleteCard = (id: string) => {
    dispatch({ type: 'DELETE_CARD', payload: id })
  }

  const handleResetForm = () => {
    dispatch({ type: 'SET_INITIAL_CARDS', payload: initialCards })
    closeResetModal()
  }

  const closeResetModal = () => setIsModalVisible(false)

  const openResetModal = () => setIsModalVisible(true)

  const prepareFormValues = (cards: CardState[]): Record<string, string> => {
    const formValues: Record<string, string> = {}

    cards.forEach(card => {
      formValues[`link${card.id}`] = card.url
      formValues[`cta${card.id}`] = card.cta
      formValues[`title${card.id}`] = card.title
    })

    return formValues
  }

  const formatCarouselItem = (carouselItem: CarouselContent, fetchedMedia: MediaData[]): CardState => {
    const domain = detectPlatformFromUrl(carouselItem.link || '')
    const posterState = getPosterState(carouselItem)
    let posterUrl = ''

    if (posterState === 'ready') {
      posterUrl = removeOptimizedPath(carouselItem.posterUrl) || ''
    } else if (posterState === 'processing') {
      const media = fetchedMedia.find(item => item.mediaUrl === carouselItem.link)
      posterUrl = media?.thumbnail || ''
    }

    return {
      id: carouselItem.id,
      order: carouselItem.order,
      url: carouselItem.link || '',
      cta: carouselItem.cta || '',
      title: carouselItem.title || '',
      posterUrl,
      mode: domain === 'media' ? 'upload' : 'url',
      status: carouselItem.status,
      videoId: carouselItem.videoId,
      errorDetails: carouselItem.errorDetails
    }
  }

  const fetchCarouselContentByDate = React.useCallback(
    async (selectedDate: dayjs.Dayjs, processedCards?: CardState[]) => {
      try {
        const content = await fetchCarouselData(selectedDate)

        if (!content.length) {
          dispatch({ type: 'SET_INITIAL_CARDS', payload: createInitialCards(INITIAL_REQUIRED_CARDS) })
          return
        }

        // Obtener URLs que necesitan metadatos
        const urlsNeedingMetadata = extractUrlsNeedingMetadata(content)

        // Hacer fetch solo si hay URLs que necesitan metadatos
        const fetchedMedia = urlsNeedingMetadata.length > 0 ? await fetchExternalMetadata(urlsNeedingMetadata) : []

        let initialCards: CardState[]

        if (processedCards) {
          initialCards = processedCards.map(processedCard => {
            const backendCard = content.find(c => c.id === processedCard.id)
            const isMediaItem = detectPlatformFromUrl(processedCard.url) === 'media'

            return {
              id: processedCard.id,
              order: processedCard.order,
              url: processedCard.url,
              cta: processedCard.cta,
              title: processedCard.title,
              posterUrl: processedCard.posterUrl,
              mode: isMediaItem ? ('upload' as 'upload') : ('url' as 'url'),
              videoId: processedCard.videoId,
              status: backendCard?.status
            }
          })
        } else {
          // Formatear los elementos del carrusel
          initialCards = content.map(carouselItem => formatCarouselItem(carouselItem, fetchedMedia))
        }

        // Preparar valores para el formulario
        const formValues = prepareFormValues(initialCards)

        dispatch({ type: 'SET_INITIAL_CARDS', payload: initialCards })
        form.setFieldsValue(formValues)
      } catch (error) {
        if (error instanceof Error && error.message?.includes('404')) {
          dispatch({ type: 'SET_INITIAL_CARDS', payload: createInitialCards(INITIAL_REQUIRED_CARDS) })
        } else {
          console.error('An unexpected error occurred:', error)
        }
      }
    },
    [form, fetchCarouselData, dispatch, fetchExternalMetadata]
  )

  const handleSubmit = async (values: any) => {
    const date = formatDate(values.date, today)

    const preparedCards = cards.map(card => {
      const link = card.mode === 'url' ? sanitizeUrl(values[`link${card.id}`]) : card.url

      return {
        ...card,
        link,
        cta: values[`cta${card.id}`],
        title: values[`title${card.id}`]
      }
    })

    const hasUniqueLinks = (cards: { link: string }[]): boolean => {
      const links = cards.map(card => card.link.trim()).filter(link => link !== '')
      return new Set(links).size === links.length
    }

    if (!hasUniqueLinks(preparedCards)) {
      message.error('No se pueden usar URLs duplicadas')
      return
    }

    try {
      setIsSubmitting(true)

      const payload = { content: preparedCards }
      const savedCarousel = await saveCarouselContent(date, payload)

      if (!savedCarousel) {
        throw new Error('Error al guardar el contenido')
      }

      // Procesar posters pendientes
      const processedCards = await Promise.all(
        savedCarousel.content.map(async (card, index) => {
          const pendingPoster = pendingPosters[card.id] // Detectamos si hay poster pendiente
          let posterUrl = card.posterUrl

          if (pendingPoster) {
            try {
              const extension = pendingPoster.fileName.includes('.')
                ? `.${pendingPoster.fileName.split('.').pop()?.toLowerCase()}`
                : ''

              if (!extension) {
                throw new Error('No se pudo obtener la extensión del archivo')
              }

              const result = await uploadPoster('image', extension, pendingPoster.file, card.videoId)

              if (!result) {
                throw new Error('No se pudo subir el poster')
              }

              posterUrl = getPosterCdnUrl(result.fileKey)
            } catch (error) {
              console.error(`Error al subir poster para card ${card.id}:`, error)
              throw new Error(`Error al subir poster para card ${card.id}:` + error)
            }
          }

          return {
            ...card,
            posterUrl
          }
        })
      )

      message.success('Contenido guardado correctamente')

      // Limpiar los posters pendientes
      dispatch({ type: 'CLEAR_PENDING_POSTERS' })

      const cardsAsCardState: CardState[] = processedCards.map(item => {
        const isMediaItem = detectPlatformFromUrl(item.link) === 'media'

        return {
          id: item.id,
          order: item.order,
          url: item.link,
          cta: item.cta,
          title: item.title,
          posterUrl: item.posterUrl,
          mode: isMediaItem ? 'upload' : 'url',
          videoId: item.videoId,
          status: item.status,
          errorDetails: item.errorDetails
        }
      })

      await fetchCarouselContentByDate(values.date || today, cardsAsCardState)
    } catch (error) {
      message.error('Ocurrió un error al guardar el contenido')
      console.error('An unexpected error occurred:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = React.useCallback(
    (id: string, field: 'url' | 'cta' | 'title' | 'posterUrl', value: string) => {
      const updates: Partial<CardState> = { [field]: value }

      if (field === 'url') {
        if (!value) {
          updates.title = ''
          updates.cta = ''
          updates.posterUrl = ''
        } else {
          const isExternalMetadata = isExternalPlatformUrl(value)

          if (isExternalMetadata) {
            updates.cta = value
          }
        }
      } else if (field === 'cta') {
        updates.cta = value
      } else if (field === 'title') {
        updates.title = value?.replace(/\n+/g, ' ') || ''
      }

      dispatch({
        type: 'UPDATE_CARD',
        payload: { id, updates }
      })
    },
    [dispatch]
  )

  // verifica si el form esta completo
  const checkFormCompletion = React.useCallback(() => {
    const completedCards = cards.every(c => c.url.trim() !== '' && c.cta.trim() !== '' && c.title.trim() !== '')

    setIsFormCompleted(completedCards && !!date)
  }, [cards, date])

  // inicializa el estado de las tarjetas
  React.useEffect(() => {
    const formValues: Record<string, string> = {}
    cards.forEach(input => {
      formValues[`link${input.id}`] = input.url
      formValues[`cta${input.id}`] = input.cta
      formValues[`title${input.id}`] = input.title
    })
    form.setFieldsValue(formValues)
  }, [cards, form])

  // carga el contenido del carrusel por fecha
  React.useEffect(() => {
    if (date) {
      fetchCarouselContentByDate(date)
    }
  }, [date, fetchCarouselContentByDate, widgetId])

  // muestra el modal de ayuda la primera vez que se carga la página
  React.useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisitedEditForm')
    if (!hasVisited) {
      setHelpModalOpen(true)
      localStorage.setItem('hasVisitedEditForm', 'true')
    }
  }, [])

  // verifica si el formulario está completo
  React.useEffect(() => {
    checkFormCompletion()
  }, [cards, checkFormCompletion])

  return (
    <Spin spinning={loading} tip='Cargando formulario...' size='large' style={{ width: '100%', height: '100%' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <Flex vertical gap='middle' style={{ height: '100%', width: '100%', overflowX: 'hidden', paddingTop: '1em' }}>
          <Row gutter={[16, 16]} justify='start'>
            <Col xs={{ span: 24, offset: 1 }} lg={{ span: 12, offset: 6 }}>
              <Form
                layout='vertical'
                form={form}
                onFinish={handleSubmit}
                onFinishFailed={() => {
                  message.error('Revisá que todas las tarjetas tengan URLs válidas de TikTok o Instagram')
                }}
                requiredMark={false}
              >
                <Flex justify='space-between' style={{ marginBottom: '1em', display: 'flex', alignItems: 'center' }}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Carga tus contenidos
                  </Typography.Title>

                  {/*  selector de fecha */}
                  <div>
                    <Form.Item
                      name='date'
                      rules={[{ type: 'object', required: true, message: 'Seleccionar una fecha' }]}
                      style={{ marginBottom: 0, flexGrow: 1, width: '100%' }}
                      initialValue={today}
                    >
                      <StyledDatePicker widgetId={widgetId} />
                    </Form.Item>
                  </div>
                </Flex>

                {/* cards */}
                <SortableContext items={cards.map(c => c.id)} strategy={rectSortingStrategy}>
                  {cards.map((card, index) => (
                    <SortableCard
                      key={card.id}
                      content={card}
                      required={index < INITIAL_REQUIRED_CARDS}
                      onFieldChange={handleInputChange}
                      isSaved={state.successfulCards.includes(card.id)}
                      onDelete={handleDeleteCard}
                      readonly={isDateInPast}
                    />
                  ))}
                </SortableContext>

                {/* agregar tarjeta */}
                <Form.Item>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <SecondaryButton
                      onClick={handleAddCard}
                      style={{ width: '100%' }}
                      icon={<Plus width={15} />}
                      disabled={isDateInPast}
                    >
                      Agregar tarjeta
                    </SecondaryButton>
                  </div>
                  {/* botón de guardar */}
                  <div
                    style={{
                      position: 'fixed',
                      top: '80%',
                      right: '6%',
                      transform: 'translateY(-50%)',
                      zIndex: 1000,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Tooltip
                      title={
                        isDateInPast
                          ? 'No se pueden guardar formularios de fechas anteriores a hoy'
                          : !isFormModified
                            ? 'Realizá cambios para poder guardar'
                            : !isFormCompleted
                              ? 'Completá todas las tarjetas'
                              : ''
                      }
                      placement='top'
                    >
                      <div className='fixed right-[6%] top-[80%] z-[1000] -translate-y-1/2'>
                        <Button
                          htmlType='submit'
                          disabled={!isFormModified || !isFormCompleted || isSubmitting || isDateInPast}
                          icon={isSubmitting ? <Spin size='small' /> : <Save width={15} />}
                          style={{
                            width: '200px',
                            height: '45px',
                            fontSize: '14px',
                            fontWeight: 600
                          }}
                        >
                          {isSubmitting ? 'Guardando...' : 'Guardar Contenido'}
                        </Button>
                      </div>
                    </Tooltip>
                  </div>

                  {/* botón restablecer con modal */}
                  <div
                    style={{
                      position: 'fixed',
                      top: '90%',
                      right: '6%',
                      transform: 'translateY(-50%)',
                      zIndex: 1000,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Tooltip title={isDateInPast ? '' : 'Deshacer cambios'} placement='top'>
                      <div>
                        <SecondaryButton
                          type='default'
                          icon={<RotateCcw width={15} />}
                          onClick={openResetModal}
                          disabled={!isFormModified || isDateInPast}
                          style={{
                            width: '200px',
                            height: '45px',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: !isFormModified || isDateInPast ? '2px solid transparent' : '2px solid red',
                            color: !isFormModified || isDateInPast ? 'rgba(0, 0, 0, 0.25)' : 'red',
                            cursor: !isFormModified || isDateInPast ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Restablecer
                        </SecondaryButton>
                      </div>
                    </Tooltip>
                    <Modal
                      title='Restablecer'
                      open={isModalVisible}
                      onCancel={closeResetModal}
                      footer={[
                        <Button key='cancel' onClick={closeResetModal}>
                          Cancelar
                        </Button>,
                        <Button key='reset' type='primary' danger onClick={handleResetForm} disabled={isDateInPast}>
                          Restablecer
                        </Button>
                      ]}
                    >
                      <p>
                        ¿Estás seguro de que deseas restablecer el formulario a su estado inicial? Todos los cambios se
                        perderán.
                      </p>
                    </Modal>
                  </div>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Flex>
        {/* botón de reload */}
        <FloatButton
          icon={<ReloadOutlined />}
          style={{ right: 100, top: 24 }}
          onClick={() => fetchCarouselContentByDate(date)}
          tooltip='Recargar contenido'
        />
        {/* botón de ayuda con modal */}
        <FloatButton
          icon={<QuestionCircleOutlined />}
          style={{ right: 50, top: 24 }}
          onClick={() => setHelpModalOpen(true)}
          tooltip='Ayuda'
        />
        <HelpModal open={isHelpModalOpen} onClose={() => setHelpModalOpen(false)} />
      </DndContext>
    </Spin>
  )
}
